// The Job Queue's rules for what a station may do next: ONE copy, used by the desktop Job Queue
// (pages/JobQueuePage.jsx) and the phone queue (mobile/screens/QueueScreen.jsx), so both offer the
// same buttons and lock the same tasks. Pure functions.
import { multiOpFlow, autoCompletePlan, fmtSeconds } from "../../utils/multiOpFlow";

// A multi-op task sitting between ops: the previous op is finished, the next one has not been
// started, and the task is Paused. This is a READY state (the next operator is supposed to pick it
// up), so the "ready only" filter and the process-in-order lock treat it as such. False for every
// ordinary task (op_count is null).
export function isAwaitingNextOp(task) {
  if (!task || !task.op_count) return false;
  const cur = task.current_op || 1;
  return cur > 1 && cur <= task.op_count && task.task_status === "Paused";
}

/** A maintenance job: job_type Maintenance or priority 0. */
export function isMaintenanceJob(job) {
  return Boolean(job) && (job.job_type === "Maintenance" || job.priority_level === 0);
}

const FABRICATION_ORDER = { Cutting: 1, Drilling: 2, Milling: 3, Tapping: 4, Lathe: 5, Welding: 6, Assembly: 7 };
const NO_DATE = 8640000000000000;

/**
 * The order a non-Laser station's tasks are listed in (and so which one the process-in-order lock
 * picks): Completed last; an operator-pinned position (Scheduling drag-to-reorder) first; then the
 * task's own priority, else the job's; then the task's due date, else the job's; multi-station
 * first; at Fabrication, the sub-process order.
 */
export function compareStationTasks(workstation) {
  return (a, b) => {
    if (!a || !b) return 0;

    const aDone = a.task_status === "Completed" || a.status === "Completed";
    const bDone = b.task_status === "Completed" || b.status === "Completed";
    if (aDone !== bDone) return aDone ? 1 : -1;

    const aPin = a.manual_sort_position ?? Number.POSITIVE_INFINITY;
    const bPin = b.manual_sort_position ?? Number.POSITIVE_INFINITY;
    if (aPin !== bPin) return aPin - bPin;

    const pA = (a.task_priority ?? a.priority_level) ?? 999;
    const pB = (b.task_priority ?? b.priority_level) ?? 999;
    if (pA !== pB) return pA - pB;

    const rawA = a.task_due_date ?? a.due_date;
    const rawB = b.task_due_date ?? b.due_date;
    const dateA = rawA ? new Date(rawA).getTime() : NO_DATE;
    const dateB = rawB ? new Date(rawB).getTime() : NO_DATE;
    const validA = Number.isNaN(dateA) ? NO_DATE : dateA;
    const validB = Number.isNaN(dateB) ? NO_DATE : dateB;
    if (validA !== validB) return validA - validB;

    if (a.multi_station !== b.multi_station) return a.multi_station ? -1 : 1;

    if (workstation === "Fabrication") {
      const aRank = FABRICATION_ORDER[a.sub_process || ""] !== undefined ? FABRICATION_ORDER[a.sub_process || ""] : 99;
      const bRank = FABRICATION_ORDER[b.sub_process || ""] !== undefined ? FABRICATION_ORDER[b.sub_process || ""] : 99;
      return aRank - bRank;
    }
    return 0;
  };
}

/**
 * The work the station must do first ("Process top job first"): the first ready item of the list
 * in its listed order. Laser items are grouped jobs (answers a job_id); every other station lists
 * tasks (answers a task_id). Non-blocking maintenance is skipped: it has the nudge / 2 PM block.
 */
export function nextAllowedId(items, workstation) {
  const nextItem = (items || []).find((item) => {
    const isMaint = item.priority_level === 0 || item.job_type === "Maintenance";
    if (isMaint && !item.block_production) return false;
    if (workstation === "Laser") {
      const t = item.tasks?.find((task) => task.workstation === "Laser");
      return t && t.status === "Pending" && !t.blocked;
    }
    return (item.task_status === "Pending" || isAwaitingNextOp(item)) && !item.blocked;
  });
  if (!nextItem) return null;
  return workstation === "Laser" ? nextItem.job_id : nextItem.task_id;
}

/** Maintenance scheduled for today and still Pending at this station: [{task_id, title}]. */
export function pendingMaintenanceFor(rows, todayStr = new Date().toLocaleDateString("en-CA")) {
  return (rows || [])
    .filter((t) => (t.priority_level === 0 || t.maintenance_definition_id != null) && t.task_status === "Pending" && t.scheduled_date === todayStr)
    .map((t) => ({ task_id: t.task_id, title: t.maintenance_title || "Scheduled Maintenance" }));
}

/** Pending maintenance blocks new Starts from 2 PM; before that it is a nudge. */
export const MAINTENANCE_DEADLINE_HOUR = 14;

/**
 * The Laser's First Article gate (PPAP FAI, GET /tasks/{id}/fai_status): `closed` while FAI is
 * required and no passing first article (or admin override) is on record, `passed` once it is.
 * Only a Pending task is gated: Resume from Paused is not (FAI is once per task). `locked` = a part
 * failed twice (an admin override opens it); `pendingParts` = the parts still to measure. No
 * faiStatus (the phone, a station other than the Laser) = never closed. Values keep the desktop's
 * own expressions (closed / passed can be a falsy non-boolean).
 */
export function faiGate({ workstation, task, faiStatus }) {
  const pending = workstation === "Laser" && Boolean(task) && task.status === "Pending";
  const closed = pending && faiStatus && faiStatus.required && !faiStatus.gate_open;
  const passed = pending && faiStatus && faiStatus.required && faiStatus.gate_open;
  const parts = (faiStatus && faiStatus.parts) || [];
  return {
    closed,
    passed,
    locked: closed && parts.some((p) => p.locked),
    pendingParts: parts.filter((p) => !p.fai_passed),
  };
}

/**
 * What the selected task may do now. `selectedTaskId` is the queue's selected task (the Laser
 * compares the job instead). `faiStatus` (the desktop's Laser) adds the First Article gate.
 * Values keep the desktop's own expressions (isOrderBlocked can be a falsy non-boolean).
 */
export function taskGates({ job, task, workstation, selectedTaskId, stationLocked, nextAllowed, pendingMaintenanceToday, faiStatus = null, hour = new Date().getHours() }) {
  const isMaintenanceTask = isMaintenanceJob(job);
  const controlsDisabled = stationLocked && !isMaintenanceTask;
  const isOrderBlocked =
    !isMaintenanceTask &&
    task.status === "Pending" &&
    nextAllowed &&
    (workstation === "Laser" ? job.job_id !== nextAllowed : selectedTaskId !== nextAllowed);
  const hasPendingMaintenance = !isMaintenanceTask && pendingMaintenanceToday && pendingMaintenanceToday.length > 0;
  const isPastDeadline = hour >= MAINTENANCE_DEADLINE_HOUR;
  return {
    isMaintenanceTask,
    controlsDisabled,
    isOrderBlocked,
    showMaintenanceNudge: hasPendingMaintenance && !isPastDeadline,
    isMaintenanceBlocked: hasPendingMaintenance && isPastDeadline && task.status === "Pending",
    maintenanceTitles: (pendingMaintenanceToday || []).map((m) => m.title).join(", "),
    opFlow: multiOpFlow(task),
    autoPlan: autoCompletePlan(task),
    fai: faiGate({ workstation, task, faiStatus }),
  };
}

/**
 * Everything a task's action area shows at a station, in order, from taskGates: ONE rule for the
 * desktop Job Queue (pages/JobQueuePage.jsx DetailView draws each step) and the phone task sheet
 * (mobile/screens/queue/TaskSheet.jsx shows the buttons and the notes).
 *
 * Resolves {steps, buttons, notes}. Every step has a `kind`, and a kind appears at most once:
 *   a button (it has `action`, a taskActions function: start | pause | finishOp | complete |
 *   autoComplete | enterMaintenance | exitMaintenance): kind start (Pending) | resume (Paused: "Resume"
 *   or "Start Op N") | pause | finishOp | complete | autoComplete | enterMaintenance |
 *   exitMaintenance, with `label`, `tone`, and `title` (tooltip) / `disabled` where they apply
 *   (a screen must honour `disabled`);
 *   or a note step (no `action`; `text` = the phone's note, the desktop draws its own):
 *   unblock (a blocked Pending task: the desktop's Unblock Task button) | stationLocked (the desktop
 *   draws nothing: its LOCKED banner says it) | maintenanceRequired (`titles` = the maintenance due)
 *   / orderLocked (the desktop's grey box) | coatingReceive (Coating: colour, Receive from Vendor,
 *   Reprint Coating PO; desktop only) | completed ("Task Completed").
 * `buttons` = the steps with an action, `notes` = the other steps' text (what the phone shows).
 * The Laser's FAI gate (gates.fai.closed) greys Start out: "🔒 Start (FAI Required)", disabled.
 */
export function taskButtons({ task, workstation, gates }) {
  const { isMaintenanceTask, controlsDisabled, isOrderBlocked, isMaintenanceBlocked, maintenanceTitles, opFlow, autoPlan, fai } = gates;
  const steps = [];
  const status = task && task.status;
  const complete = { kind: "complete", action: "complete", label: opFlow.multiOp ? "Complete Task (last op)" : "Complete", tone: "danger" };
  const finish = (title) => ({ kind: "finishOp", action: "finishOp", label: `Finish ${opFlow.label}`, tone: "primary", title });
  const auto = autoPlan
    ? {
      kind: "autoComplete", action: "autoComplete", label: autoPlan.label, tone: "info",
      title: autoPlan.multiOp
        ? `Record ${autoPlan.opLabel} at ${fmtSeconds(autoPlan.bookedSeconds)} (its share of the estimate) without a timer` +
          (autoPlan.lastOp ? " and complete the task." : ` and hand the task to operation ${autoPlan.nextOp}. Does not complete the task.`)
        : undefined,
    }
    : null;

  if (status === "Maintenance") {
    steps.push({ kind: "exitMaintenance", action: "exitMaintenance", label: "End Maintenance", tone: "warning" });
  } else if (status === "Completed") {
    steps.push({ kind: "completed", text: "Task completed." });
  } else if (status === "Pending") {
    if (task.blocked) {
      steps.push({ kind: "unblock", text: "This task is blocked. Unblock it at the station." });
    } else if (controlsDisabled) {
      steps.push({ kind: "stationLocked", text: "The station is locked until its critical maintenance is done." });
    } else {
      if (isMaintenanceBlocked) steps.push({ kind: "maintenanceRequired", text: `🔒 MAINTENANCE REQUIRED. Complete: ${maintenanceTitles}`, titles: maintenanceTitles });
      else if (isOrderBlocked) steps.push({ kind: "orderLocked", text: "🔒 LOCKED. Process the top job first." });
      else if (workstation === "Coating") steps.push({ kind: "coatingReceive", text: "Receive from the vendor at the station (or Coating Manifests)." });
      else if (fai && fai.closed) steps.push({ kind: "start", action: "start", label: "🔒 Start (FAI Required)", tone: "secondary", disabled: true, title: "First Article Inspection required before Start" });
      else steps.push({ kind: "start", action: "start", label: "Start", tone: "success" });
      // As on the desktop, Auto-Complete is not held back by the order lock.
      if (workstation !== "Laser" && workstation !== "Coating" && !isMaintenanceTask && !isMaintenanceBlocked && auto) steps.push(auto);
    }
  } else if (status === "In Progress") {
    steps.push({ kind: "pause", action: "pause", label: "Pause", tone: "warning" });
    // Ops still to run: Finish Op is the only way forward (the API refuses Complete with a 409).
    steps.push(opFlow.hasMoreOps
      ? finish(`Bank your time on operation ${opFlow.currentOp} and hand the task to operation ${opFlow.currentOp + 1}. Does not complete the task.`)
      : complete);
  } else if (status === "Paused") {
    // Between ops this is the next operator picking the task up, so the label names the op.
    const nextOp = opFlow.multiOp && opFlow.currentOp > 1;
    if (!controlsDisabled) {
      steps.push({
        kind: "resume", action: "start", label: nextOp ? `Start ${opFlow.label}` : "Resume", tone: "success",
        title: nextOp ? `Pick up ${opFlow.label} — the previous operation is already finished.` : undefined,
      });
    }
    // Not held back by a station lock: it starts no work (the pause banked the time).
    if (opFlow.hasMoreOps) {
      steps.push(finish(`Mark operation ${opFlow.currentOp} finished and hand the task to operation ${opFlow.currentOp + 1}. Your time is already banked. Does not complete the task.`));
    }
    // Only an untouched op between ops (autoPlan.multiOp); it books work, so a lock holds it back.
    if (!controlsDisabled && !isMaintenanceTask && !isMaintenanceBlocked && auto && autoPlan.multiOp) steps.push(auto);
    if (!opFlow.hasMoreOps) steps.push(complete);
  }
  if (status === "In Progress" || status === "Paused") {
    steps.push({ kind: "enterMaintenance", action: "enterMaintenance", label: "Maintenance", tone: "secondary" });
  }
  return {
    steps,
    buttons: steps.filter((st) => st.action),
    notes: steps.filter((st) => !st.action).map((st) => st.text),
  };
}
